import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import AuthService from "../../modules/Admin/services/AuthService";

const STORAGE_KEY_BASE = "nf_product_tour_v1_done_uid_";

/**
 * Shown once per user id (localStorage). Explains sidebar areas and key workflows.
 */
export default function FirstLoginTour() {
  const [visible, setVisible] = useState(false);
  const [step, setStep] = useState(0);

  const steps = useMemo(() => {
    const role = parseInt(AuthService.getLoggedInRoleId(), 10);
    const isSa = role === 1;
    const isEmp = role === 4;

    const list = [
      {
        title: "Welcome",
        body: "Here's a quick tour of Next Follow Up—leads, pipelines, attendance, and where admins manage the workspace.",
      },
      {
        title: "Dashboard & filters",
        body: "Use the date range and Search on the dashboard to refresh KPI cards, funnel counts, and follow-up shortcuts.",
      },
      {
        title: "Leads workspace",
        body: "Leads is your master list. Assigned Leads is for daily calls, visits, status updates, and property notes tied to prospects.",
      },
      {
        title: "Kanban pipeline",
        body: "Kanban mirrors your sales stages so the team sees workflow at a glance. Admins configure stages under Pipeline Stages.",
      },
    ];

    if (!isEmp) {
      list.push({
        title: "Reports",
        body: "Open Reports for funnel and outcomes over time. Tenant Settings cover holidays, timezone, and workspace basics.",
      });
    }

    if (!isEmp && !isSa) {
      list.push({
        title: "Admin tools",
        body: "Team Attendance and user onboarding appear in your admin sidebar; employees see a slimmer menu focused on their own pipeline.",
      });
    }

    if (isSa) {
      list.push({
        title: "Super Admin",
        body: "Clients, subscriptions, and platform integrations live under the Super Admin menu for multi-agency setup.",
      });
    }

    list.push({
      title: "You're set",
      body: "Explore anytime from the sidebar. You can dismiss this tour with Skip or Finish— it won't show again on this browser profile.",
    });

    return list;
  }, []);

  useEffect(() => {
    const uid = AuthService.getLoggedInUserId();
    if (!uid) return;
    const key = `${STORAGE_KEY_BASE}${uid}`;
    try {
      if (localStorage.getItem(key)) return;
    } catch {
      return;
    }
    setVisible(true);
  }, []);

  const persistDone = useCallback(() => {
    const uid = AuthService.getLoggedInUserId();
    if (!uid) return;
    try {
      localStorage.setItem(`${STORAGE_KEY_BASE}${uid}`, "1");
    } catch {
      /* ignore */
    }
    setVisible(false);
  }, []);

  const footer = (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: 12,
        flexWrap: "wrap",
      }}
    >
      <Button type="button" label="Skip" text onClick={persistDone} />
      <div style={{ display: "flex", gap: 8 }}>
        {step > 0 ? (
          <Button type="button" label="Back" outlined onClick={() => setStep((s) => Math.max(0, s - 1))} />
        ) : null}
        <Button
          type="button"
          label={step >= steps.length - 1 ? "Get started" : "Next"}
          onClick={() => {
            if (step >= steps.length - 1) persistDone();
            else setStep((s) => s + 1);
          }}
        />
      </div>
    </div>
  );

  if (!steps.length) return null;

  return (
    <Dialog
      header={steps[step]?.title ?? "Tour"}
      visible={visible}
      modal
      style={{ width: "min(440px, 94vw)" }}
      footer={footer}
      closable={false}
      draggable={false}
      onHide={persistDone}
      blockScroll
    >
      <p style={{ margin: 0, lineHeight: 1.55, color: "#596173", fontSize: "14px" }}>{steps[step]?.body}</p>
      <p style={{ margin: "14px 0 0", fontSize: "12px", color: "#8392ab" }}>
        Step {step + 1} of {steps.length}
      </p>
    </Dialog>
  );
}
